// NonAdminLeftSidebar.jsx - Using roleStorage utilities
import React, {useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import { useLocation, Link } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import SidebarButton from "@components/Sidebar/Others/SidebarButton";
import { CircularProgress, Divider } from "@mui/material";
import { TaleLoadingFallback } from "@components/TaleLoadingFallback";
import { getNonAdminLeftSideBar } from "@/utils/featureFlipDependepent";
import { FF } from "@/utils/featureFlip";
import authService from "@services/auth";
import {getUserType, isCompanyUser, isNormalUser} from "@/utils/roleStorage";
import eventBus from "@/utils/eventBus";
import { useTranslation } from "@/hooks/useTranslation";


export const NonAdminLeftSidebar = ({ sidebarVisible }) => {
    const location = useLocation();
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const { logout: auth0Logout } = useAuth0();
    const { t } = useTranslation();

    // Get role from storage utilities
    const role = useMemo(() => getUserType(), []);
    // Check user type using storage utilities
    const isnormalUser = useMemo(() => isNormalUser(), []);
    const iscompanyUser = useMemo(() => isCompanyUser(), []);

    const handleLogout = async () => {
        const authType = localStorage.getItem("auth_type");

        if (authType === "local") {
            // Local auth logout
            authService.logout();
        } else {
            // Auth0 logout
            // Clear local storage first
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            localStorage.removeItem("auth_type");
            localStorage.removeItem("user");

            await auth0Logout({
                logoutParams: {
                    returnTo: `${process.env.REACT_APP_WEBSITE}/login`,
                },
            });
        }
    };

    const sidebarButtons = useMemo(() => getNonAdminLeftSideBar(role), [role]);

    const activeButtonsMapping = useMemo(
        () => ({
            dashboard: [""],
            surveys: ["surveys", "survey"],
            focusGroup: ["focus-groups", "focus-group"],
            awards: ["awards"],
            biAnalysis: ["BI"],
            ...(isnormalUser || FF.USE_ASSISTANCE
                ? { assistance: ["assistance"] }
                : {}),
            invoices: ["factures"],
            reports: ["reports"],
            ...(FF.USE_EXCLUSIVITY ? { exclusivity: ["exclusivity"] } : {}),
        }),
        [isnormalUser]
    );

    const activeButton = useMemo(() => {
        const path = location.pathname.split("/").at(1);
        const keys = Object.keys(activeButtonsMapping);
        return keys.find((k) => activeButtonsMapping[k].some((l) => l === path));
    }, [activeButtonsMapping, location.pathname]);

    const ref = useRef(null);
    const [itemWidth, setitemWidth] = useState(0);
    const [isLayoutSidebarVisible, setIsLayoutSidebarVisible] = useState(true);

    // Track the fixed panel's width continuously (not just on toggle): its
    // width depends on the translated labels, so a UI-language switch resizes
    // it and the in-flow spacer must follow or the main content slides under it.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const measure = () => setitemWidth(el.getBoundingClientRect().width);
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [sidebarVisible, isLayoutSidebarVisible]);

    useEffect(() => {
        eventBus.subscribe("layout-sidebar-visible", setIsLayoutSidebarVisible);
        return () => eventBus.unsubscribe("layout-sidebar-visible");
    }, []);

    if (!isLayoutSidebarVisible) return null;

    return (
        <div
            className={`z-50 transition-all`}
            style={{
                minWidth: sidebarVisible ? itemWidth : 60,
            }}
        >
            <div
                ref={ref}
                className="bg-white rounded-2xl min-h-[calc(100vh-var(--header-h,100px)-16px)] max-h-[calc(100vh-var(--header-h,100px)-16px)] flex flex-col justify-between fixed top-[var(--header-h,100px)] left-4 rtl:left-auto rtl:right-4 z-40 overflow-y-auto"
            >
                <div className="grow justify-between flex flex-col">
                    <div
                        style={{
                            borderRadius: 10,
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            gap: 1,
                            display: "inline-flex",
                        }}
                        className="p-2"
                    >
                        {!role ? (
                            <TaleLoadingFallback className="pt-20">
                                <CircularProgress />
                            </TaleLoadingFallback>
                        ) : (
                            sidebarButtons.map((e) => (
                                <Link key={e.key} to={e.url} className="w-full">
                                    <SidebarButton
                                        role={role}
                                        label={t(`nav.sidebar.${e.key}`)}
                                        icon={e.icon}
                                        active={activeButton === e.key}
                                        expand={sidebarVisible}
                                    />
                                </Link>
                            ))
                        )}
                    </div>

                    <div
                        style={{
                            borderRadius: 10,
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            alignItems: "flex-start",
                            gap: 1,
                            display: "inline-flex",
                        }}
                        className="p-2"
                    >
                        {[
                            ...(iscompanyUser && FF.USE_EXCLUSIVITY
                                ? [
                                    {
                                        key: "exclusivity",
                                        icon: "/assets/sidebar/exclusivity.svg",
                                        url: "/exclusivity",
                                    },
                                ]
                                : []),
                            ...(isnormalUser || FF.USE_ASSISTANCE
                                ? [
                                    {
                                        key: "assistance",
                                        icon: "/assets/sidebar/assistance.svg",
                                        url: "/assistance",
                                    },
                                ]
                                : []),
                            {
                                key: "logout",
                                icon: "/assets/sidebar/logout.svg",
                                url: null,
                            },
                        ].map((e) => {
                            const button = (
                                <SidebarButton
                                    key={e.key}
                                    role={role}
                                    label={t(`nav.sidebar.${e.key}`)}
                                    icon={e.icon}
                                    active={activeButton === e.key}
                                    onClick={() =>
                                        e.key === "logout" ? handleLogout() : undefined
                                    }
                                    expand={sidebarVisible}
                                />
                            );
                            return e.key === "logout" ? (
                                button
                            ) : (
                                <Link key={e.key} to={e.url} className="w-full">
                                    {button}
                                </Link>
                            );
                        })}
                    </div>
                </div>
                <div className="flex flex-col gap-1">
                    <Divider />
                    <div
                        className={`p-2 flex ${
                            sidebarVisible ? "gap-1" : "flex-col"
                        } justify-center items-center`}
                        style={{
                            color: "#666666",
                            fontSize: 12,
                            fontFamily: "revert",
                            fontWeight: "400",
                        }}
                    >
                        {!sidebarVisible ? null : <span>©{currentYear} </span>}
                        <span>Tale. {!sidebarVisible ? null : t("common.allRightsReserved")}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};
